import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { generateJWTToken } from 'src/app/core/helpers/jwt-token.utils';
import { SessionService } from 'src/app/core/backend-adapter/session.service';

@Injectable({ providedIn: 'root' })
export class LocationsFilterService {
  private baseUrl ='https://io.renderseo.com/access/filters';
    /*window.location.hostname.includes('localhost') || window.location.hostname.includes('dev')
      ? 'https://dev-io.renderseo.com/access/filters'
      : 'https://io.renderseo.com/access/filters';*/

  // Cache for locations data
  private filtersCache: { [key: string]: { data: any; timestamp: number } } = {};
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes

  constructor(private http: HttpClient, private sessionService: SessionService) {}

  getFilters(accountId: number, userId: number, useCache: boolean = true): Promise<any> {
    const cacheKey = `${accountId}_${userId}`;
    console.log(`🔍 getFilters called - accountId: ${accountId}, userId: ${userId}, cacheKey: ${cacheKey}, useCache: ${useCache}`);
    
    // Check cache first
    if (useCache && this.filtersCache[cacheKey]) {
      const cached = this.filtersCache[cacheKey];
      const now = Date.now();
      const age = now - cached.timestamp;
      // Return cached data if it's still valid
      if (age < this.cacheTimeout) {
        console.log(`✅ Using cached locations data (age: ${Math.round(age / 1000)}s, timeout: ${this.cacheTimeout / 1000}s)`);
        console.log(`📦 Cache contents: ${Object.keys(this.filtersCache).length} entries`);
        return Promise.resolve(cached.data);
      } else {
        console.log(`⚠️ Cache expired (age: ${Math.round(age / 1000)}s, timeout: ${this.cacheTimeout / 1000}s), fetching fresh data`);
        // Cache expired, remove it
        delete this.filtersCache[cacheKey];
      }
    } else if (useCache) {
      console.log(`ℹ️ No cache found for key "${cacheKey}", fetching locations data from API`);
      console.log(`📦 Current cache keys: ${Object.keys(this.filtersCache).join(', ') || 'none'}`);
    } else {
      console.log(`🔄 Cache disabled (useCache=false), fetching fresh data`);
    }

    const payloadObj = {
      iss: 'https://platform.renderseo.com',
      sub: userId,
      account_id: accountId,
      iat: new Date().getTime(),
      exp: new Date().getTime() + 3 * 6000, // ~18s
    };

    const jwtToken = generateJWTToken(payloadObj, '2c4c4d06f0b35b921039d9df2725205d981ac00bf196eb71b02ce7571432e30a');

    const headers = new HttpHeaders().set('Authorization', `${jwtToken}`);

    console.log(`🌐 Fetching locations from API (cacheKey: ${cacheKey})...`);
    return this.http.get<any>(this.baseUrl, { headers }).toPromise().then((data) => {
      // Cache the result
      this.filtersCache[cacheKey] = {
        data: data,
        timestamp: Date.now()
      };
      console.log(`✅ Locations data fetched and cached (cacheKey: ${cacheKey})`);
      console.log(`📦 Total cache entries: ${Object.keys(this.filtersCache).length}`);
      return data;
    });
  }

  // Method to preload filters (checks cache first, only fetches if cache is missing or expired)
  preloadFilters(accountId: number, userId: number): Promise<any> {
    // Use getFilters with cache enabled - it will use cache if available, or fetch and cache if not
    // This ensures we don't unnecessarily clear valid cache
    return this.getFilters(accountId, userId, true);
  }

  // Method to get filtered locations based on query parameters
  getFilteredLocations(accountId: number, userId: number, queryParams: { [key: string]: string }): Promise<any> {
    const payloadObj = {
      iss: 'https://platform.renderseo.com',
      sub: userId,
      account_id: accountId,
      iat: new Date().getTime(),
      exp: new Date().getTime() + 3 * 6000, // ~18s
    };

    const jwtToken = generateJWTToken(payloadObj, '2c4c4d06f0b35b921039d9df2725205d981ac00bf196eb71b02ce7571432e30a');
    const headers = new HttpHeaders().set('Authorization', `${jwtToken}`);

    // Build query string from parameters
    const queryString = Object.keys(queryParams)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
      .join('&');

    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
    
    console.log('=== API REQUEST ===');
    console.log('Full URL:', url);
    console.log('Query params object:', queryParams);

    return this.http.get<any>(url, { headers }).toPromise();
  }
}
